import { useState, useEffect } from 'react';
import {
  signIn,
  signUp,
  confirmSignUp,
  signOut,
  getCurrentUser,
  fetchAuthSession,

  resendSignUpCode,
  signInWithRedirect,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

type Screen = 'loading' | 'signin' | 'signup' | 'confirm' | 'app';

interface Props {
  children: (opts: { email: string; getToken: () => Promise<string>; onSignOut: () => void }) => React.ReactNode;
}

const AUTH_STYLES = `
.au { --ink:#17363B; --ink-soft:#667679; --line:#DDD4CA; --line-soft:#EEE6DD;
  --paper:#FCF5EC; --paper-raised:#F7F2EC; --surface:#FFFFFF; --surface-raised:#F7F2EC;
  --mark:#FF5C3D; --mark-soft:rgba(255,92,61,.20);
  --brand-teal:#086574; --on-accent:#FFFFFF; --placeholder:#9AA3AF;
  --sans:'Inter',system-ui,-apple-system,sans-serif;
  --mono:'Manrope',system-ui,sans-serif;
  --hand:'Caveat',cursive;
  color-scheme:light;
  font-family:var(--sans); color:var(--ink); background:var(--paper);
  min-height:100vh; display:flex; align-items:center; justify-content:center; padding:20px; box-sizing:border-box; }
.au *, .au *::before, .au *::after { box-sizing:border-box; }
@media (prefers-color-scheme:dark) {
  .au { --ink:#F8F0E7; --ink-soft:#B8C4C3; --line:#344548; --line-soft:rgba(52,69,72,.52);
    --paper:#10191B; --paper-raised:#203034; --surface:#182427; --surface-raised:#203034;
    --mark:#FF7A61; --mark-soft:rgba(255,122,97,.18);
    --brand-teal:#7FD7DD; --on-accent:#10191B; --placeholder:#829291;
    color-scheme:dark; }
}
[data-theme="dark"] .au, .au[data-theme="dark"] { --ink:#F8F0E7; --ink-soft:#B8C4C3; --line:#344548; --line-soft:rgba(52,69,72,.52);
  --paper:#10191B; --paper-raised:#203034; --surface:#182427; --surface-raised:#203034;
  --mark:#FF7A61; --mark-soft:rgba(255,122,97,.18);
  --brand-teal:#7FD7DD; --on-accent:#10191B; --placeholder:#829291;
  color-scheme:dark; }
[data-theme="light"] .au, .au[data-theme="light"] { --ink:#17363B; --ink-soft:#667679; --line:#DDD4CA; --line-soft:#EEE6DD;
  --paper:#FCF5EC; --paper-raised:#F7F2EC; --surface:#FFFFFF; --surface-raised:#F7F2EC;
  --mark:#FF5C3D; --mark-soft:rgba(255,92,61,.20);
  --brand-teal:#086574; --on-accent:#FFFFFF; --placeholder:#9AA3AF;
  color-scheme:light; }
.au-card { background:var(--surface); border:1px solid var(--line); border-radius:16px; padding:32px; width:100%; max-width:380px; display:flex; flex-direction:column; gap:20px; }
.au-brand { display:flex; align-items:flex-end; }
.au-logo { display:block; width:158px; max-width:100%; height:auto; }
.au-brand .sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
.au-title { font-size:16px; font-weight:640; color:var(--ink); }
.au-fields { display:flex; flex-direction:column; gap:10px; }
.au-field { display:flex; flex-direction:column; gap:5px; }
.au-label { font-size:12px; font-weight:560; color:var(--ink-soft); }
.au-input { border:1px solid var(--line); border-radius:9px; padding:10px 13px; font-size:14px; font-family:var(--sans); color:var(--ink); background:var(--paper); width:100%; transition:border-color .15s; }
.au-input:focus { outline:none; border-color:var(--ink); }
.au-input::placeholder { color:var(--placeholder); }
.au-btn { border:none; background:var(--mark); color:var(--on-accent); border-radius:9px; padding:11px 16px; font-size:14px; font-weight:560; font-family:var(--sans); cursor:pointer; transition:filter .15s; width:100%; }
.au-btn:hover { filter:brightness(1.06); }
.au-btn:disabled { opacity:.5; cursor:default; filter:none; }
.au-social-btn { border:1px solid var(--line); background:var(--surface); color:var(--ink); border-radius:9px; padding:10px 16px; font-size:14px; font-weight:560; font-family:var(--sans); cursor:pointer; width:100%; display:flex; align-items:center; justify-content:center; gap:9px; transition:border-color .15s, background .15s; }
.au-social-btn:hover { border-color:var(--ink-soft); background:var(--surface-raised); }
.au-google-mark { width:18px; height:18px; display:block; }
.au-divider { display:flex; align-items:center; gap:10px; color:var(--ink-soft); font-size:11px; font-family:var(--mono); text-transform:uppercase; }
.au-divider::before, .au-divider::after { content:""; height:1px; background:var(--line-soft); flex:1; }
.au-link { background:none; border:none; color:var(--mark); font-size:13px; font-family:var(--sans); cursor:pointer; padding:0; text-decoration:underline; }
.au-error { font-size:13px; color:var(--mark); background:var(--mark-soft); border-radius:8px; padding:9px 12px; }
.au-hint { font-size:12px; color:var(--ink-soft); font-family:var(--mono); }
.au-row { display:flex; align-items:center; justify-content:center; gap:6px; font-size:13px; color:var(--ink-soft); }
.au-switch { font-size:11px; color:var(--ink-soft); text-align:center; }
`;

export default function AuthGate({ children }: Props) {
  const [screen, setScreen] = useState<Screen>('loading');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    async function resolveUser() {
      try {
        const u = await getCurrentUser();
        // Set app screen immediately — email display is best-effort
        setScreen('app');
        try {
          const loginId = u.signInDetails?.loginId;
          const session = await fetchAuthSession();
          const payload = session.tokens?.idToken?.payload;
          const display = (payload?.email as string) ?? (payload?.name as string) ?? (loginId?.includes('@') ? loginId : '');
          setEmail(display);
        } catch {
          // email display optional, don't block app access
        }
      } catch {
        setScreen('signin');
      }
    }

    // Amplify processes the OAuth code exchange async after redirect. Hub fires
    // 'signedIn' when done — at that point getCurrentUser() will succeed.
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') {
        resolveUser();
      }
    });

    resolveUser();
    return unsubscribe;
  }, []);

  async function getToken(): Promise<string> {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? '';
  }

  async function handleSignIn() {
    setError(''); setBusy(true);
    try {
      await signIn({ username: email, password });
      setScreen('app');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Sign in failed');
    } finally { setBusy(false); }
  }

  async function handleSignUp() {
    setError(''); setBusy(true);
    if (password !== confirmPassword) { setError('Passwords do not match'); setBusy(false); return; }
    try {
      await signUp({ username: email, password, options: { userAttributes: { email } } });
      setPendingEmail(email);
      setScreen('confirm');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Sign up failed');
    } finally { setBusy(false); }
  }

  async function handleConfirm() {
    setError(''); setBusy(true);
    try {
      await confirmSignUp({ username: pendingEmail, confirmationCode: code });
      await signIn({ username: pendingEmail, password });
      setEmail(pendingEmail);
      setScreen('app');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Confirmation failed');
    } finally { setBusy(false); }
  }

  async function handleResend() {
    try { await resendSignUpCode({ username: pendingEmail }); }
    catch { /* ignore */ }
  }

  async function handleSignOut() {
    await signOut({ global: true });
    setEmail(''); setPassword(''); setConfirmPassword(''); setCode('');
    setScreen('signin');
  }

  async function handleGoogleSignIn() {
    setError('');
    setBusy(true);
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Google sign in failed');
      setBusy(false);
    }
  }

  function onKey(e: React.KeyboardEvent, action: () => void) {
    if (e.key === 'Enter') action();
  }

  if (screen === 'loading') {
    return (
      <div className="au">
        <style>{AUTH_STYLES}</style>
        <div className="au-card">
          <div className="au-brand">
            <img className="au-logo" src="/brand-assets/wingmate-logo-horizontal-gray-baseline-gap30-transparent.png" alt="" aria-hidden="true" />
            <span className="sr-only">WingMate</span>
          </div>
          <div className="au-hint">Loading…</div>
        </div>
      </div>
    );
  }

  if (screen === 'app') {
    return <>{children({ email, getToken, onSignOut: handleSignOut })}</>;
  }

  if (screen === 'confirm') {
    return (
      <div className="au">
        <style>{AUTH_STYLES}</style>
        <div className="au-card">
          <div className="au-brand">
            <img className="au-logo" src="/brand-assets/wingmate-logo-horizontal-gray-baseline-gap30-transparent.png" alt="" aria-hidden="true" />
            <span className="sr-only">WingMate</span>
          </div>
          <div className="au-title">Check your email</div>
          <div className="au-hint">We sent a confirmation code to {pendingEmail}</div>
          {error && <div className="au-error">{error}</div>}
          <div className="au-fields">
            <div className="au-field">
              <label className="au-label">Confirmation code</label>
              <input className="au-input" autoFocus type="text" inputMode="numeric" placeholder="123456"
                value={code} onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => onKey(e, handleConfirm)} />
            </div>
          </div>
          <button className="au-btn" onClick={handleConfirm} disabled={busy || !code}>
            {busy ? 'Confirming…' : 'Confirm account'}
          </button>
          <div className="au-row">
            Didn't get it? <button className="au-link" onClick={handleResend}>Resend code</button>
          </div>
        </div>
      </div>
    );
  }

  if (screen === 'signup') {
    return (
      <div className="au">
        <style>{AUTH_STYLES}</style>
        <div className="au-card">
          <div className="au-brand">
            <img className="au-logo" src="/brand-assets/wingmate-logo-horizontal-gray-baseline-gap30-transparent.png" alt="" aria-hidden="true" />
            <span className="sr-only">WingMate</span>
          </div>
          <div className="au-title">Create your account</div>
          {error && <div className="au-error">{error}</div>}
          <button className="au-social-btn" onClick={handleGoogleSignIn} disabled={busy}>
            <svg className="au-google-mark" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
            </svg>
            Continue with Google
          </button>
          <div className="au-divider">or</div>
          <div className="au-fields">
            <div className="au-field">
              <label className="au-label">Email</label>
              <input className="au-input" autoFocus type="email" placeholder="you@example.com"
                value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="au-field">
              <label className="au-label">Password</label>
              <input className="au-input" type="password" placeholder="8+ characters, upper + lower + number"
                value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <div className="au-field">
              <label className="au-label">Confirm password</label>
              <input className="au-input" type="password" placeholder="Same again"
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyDown={(e) => onKey(e, handleSignUp)} />
            </div>
          </div>
          <button className="au-btn" onClick={handleSignUp} disabled={busy || !email || !password || !confirmPassword}>
            {busy ? 'Creating account…' : 'Create account'}
          </button>
          <div className="au-row">
            Already have one? <button className="au-link" onClick={() => { setScreen('signin'); setError(''); }}>Sign in</button>
          </div>
        </div>
      </div>
    );
  }

  // signin
  return (
    <div className="au">
      <style>{AUTH_STYLES}</style>
      <div className="au-card">
        <div className="au-brand">
          <img className="au-logo" src="/brand-assets/wingmate-logo-horizontal-gray-baseline-gap30-transparent.png" alt="" aria-hidden="true" />
          <span className="sr-only">WingMate</span>
        </div>
        <div className="au-title">Sign in</div>
        {error && <div className="au-error">{error}</div>}
        <button className="au-social-btn" onClick={handleGoogleSignIn} disabled={busy}>
          <svg className="au-google-mark" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
          </svg>
          Continue with Google
        </button>
        <div className="au-divider">or</div>
        <div className="au-fields">
          <div className="au-field">
            <label className="au-label">Email</label>
            <input className="au-input" autoFocus type="email" placeholder="you@example.com"
              value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="au-field">
            <label className="au-label">Password</label>
            <input className="au-input" type="password" placeholder="Your password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => onKey(e, handleSignIn)} />
          </div>
        </div>
        <button className="au-btn" onClick={handleSignIn} disabled={busy || !email || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <div className="au-row">
          No account? <button className="au-link" onClick={() => { setScreen('signup'); setError(''); }}>Create one</button>
        </div>
      </div>
    </div>
  );
}
